import * as React from 'react';
import type { Ind } from '../../MedicareProAPI/Entities/Ind/Ind';
import type { IndAPIInclude } from '../../MedicareProAPI/Repositories/Ind/IndRepository';
import { IndRepository } from '../../MedicareProAPI/Repositories/Ind/IndRepository';
import { IndSearch } from './Form/Field/IndSearch';
import { Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { IndNote } from '../../MedicareProAPI/Entities/Ind/IndNote';
import { IndNoteRepository } from '../../MedicareProAPI/Repositories/Ind/IndNoteRepository';
import { NoteContentDisplay } from './NoteContentDisplay';
import ProfileNotesLogsCalls from './ProfileNotesLogsCalls';
import { SMSChat } from './SMSChat';
import { EmailsCollection } from './EmailsCollection';
import ProfileNotesLogsSMS from './ProfileNotesLogsSMS';
import ProfileNotesLogs from './ProfileNotesLogs';
import { PROFILE_LOG_PORTAL_TAB_LABELS } from '../Language/en';
import type { User } from '../../MedicareProAPI/Entities/User/User';
import { SIDETAB_POSITION, SideTab, SIDETAB_SIZE } from './SideTab';
import { Utilities } from '../../MedicareProAPI/Utilities/Utilities';
import { IntegrationManager } from './IntegrationManager';
import type { INTEGRATION_TYPE } from '../Constants/Integrations';
import { INTEGRATION_OPTIONS } from '../Constants/Integrations';
import { AgentIndProfileCmsFormsPanel } from '../Panels/AgentIndProfileCmsFormsPanel';
import AgentEnrollmentPanel from '../Panels/AgentEnrollmentPanel';
import { UserFlagRepository } from '../../MedicareProAPI';
import type { UserFlag } from '../../MedicareProAPI/Entities/User/UserFlag';
import { CalendarCreateEventButton } from '../Buttons/CalendarCreateEventButton';
import { AlertModal } from '../Modals/AlertModal';
import { ActivityModal } from '../Modals/ActivityModal';
import { UserActivity } from '../../MedicareProAPI/Entities/User/UserActivity';
import type { ActionType } from '../../MedicareProAPI/Entities/User/UserActionSuggestion';
import {
  ACTION_TYPES,
  STATUS_TYPES,
  StatusType,
} from '../../MedicareProAPI/Entities/User/UserActionSuggestion';
import { IndFlowHistoryProfile } from './Flows/IndFlowHistoryProfile';
interface IndProfileMiniProps {
  user: User;
  userId: number;
  userAccessToken: string;
  indId: number;
  include?: IndAPIInclude[];
  onSizeChange?: (size: SIDETAB_SIZE) => void;
  usersAgencyId?: number;
  directUplineId?: number;
  absoluteUplineId?: number;
  staffId?: number;
  operatorName?: string;
}

export enum PROFILE_LOG_TAB {
  CALL_LOG = 0,
  SMS_LOG = 1,
  OTHER_LOGS = 2,
}

/**
 * Component that shows Ind information in a condensed view
 *
 * @component
 * @author Andrew Brown <abrown@medicareprocrm.com>
 * @date Created: 2025-07-24
 * @lastUpdated
 * @version 1.1.0
 */

export const IndProfileMini = ({
  user,
  userId,
  userAccessToken,
  indId,
  include,
  onSizeChange,
  usersAgencyId,
  directUplineId,
  absoluteUplineId,
  staffId,
  operatorName,
}: IndProfileMiniProps): React.JSX.Element => {
  const individualRepository = new IndRepository();
  individualRepository.setAccessToken(userAccessToken);

  const individualNoteRepository = new IndNoteRepository();
  individualNoteRepository.setAccessToken(userAccessToken);

  const userFlagRepository = new UserFlagRepository();
  userFlagRepository.setAccessToken(userAccessToken);

  const [userFlags, setUserFlags] = React.useState<Array<UserFlag>>([]);
  const [individual, setInd] = React.useState<Ind>();
  const [selectedSection, setSelectedSection] = React.useState<number>();
  const [selectedLogSection, setSelectedLogSection] = React.useState<PROFILE_LOG_TAB>(
    PROFILE_LOG_TAB.CALL_LOG
  );
  const [noteExpanded, setNoteExpanded] = React.useState(false);

  const [indNotes, setIndNotes] = React.useState<Array<IndNote>>(null);
  const [isAddingNote, setIsAddingNote] = React.useState(false);

  const [showIntegrationSetup, setShowIntegrationSetup] = React.useState<boolean>(false);
  const [allowedIntegrationServices, setAllowedIntegrationServices] = React.useState<Array<string>>(
    []
  );
  const [allowedVoIPServices, setAllowedVoIPServices] = React.useState<Array<string>>([]);
  const [allowedSyncServices, setAllowedSyncServices] = React.useState<Array<string>>([]);
  const [refreshKey, setRefreshKey] = React.useState(0);

  const [isMakingActivity, setIsMakingActivity] = React.useState<boolean>(false);
  const [isMakingAlert, setIsMakingAlert] = React.useState<boolean>(false);
  const [isMakingAppointment, setIsMakingAppointment] = React.useState<boolean>(false);
  const [tempActivity, setTempActivity] = React.useState<UserActivity>();

  const notesLimit = 10;
  const notesOffset = 0;
  const MAX_LENGTH = 300;

  const colorToMeaning: Record<string, string> = Object.fromEntries(
    userFlags.map((f) => [f.getFlagColor(), f.getFlagMeaning()])
  );
  console.log(colorToMeaning);
  const sections = [
    {
      title: 'Policies',
      value: 'policies',
      icon: 'fa-file-signature',
    },
    {
      title: 'Notes',
      value: 'notes',
      icon: 'fa-pencil',
    },
    {
      title: 'SMS',
      value: 'sms',
      icon: 'fa-comment-dots',
    },
    {
      title: 'Email',
      value: 'email',
      icon: 'fa-at',
    },
    {
      title: 'SOA/Consent',
      value: 'soa',
      icon: 'fa-file-check',
    },
    {
      title: 'Enroll',
      value: 'enroll',
      icon: 'fa-rocket',
    },
    {
      title: 'Logs',
      value: 'logs',
      icon: 'fa-cog',
    },
    {
      title: 'Flows',
      value: 'flows',
      icon: 'fa-stream',
    },
  ];

  /**
   * Handle a user-initiated action on a suggestion.
   *
   * Triggers the appropriate modal/workflow based on the selected action
   * (appointment, alert, activity, or dismissal).
   *
   * @param id - Suggestion identifier.
   * @param action - Selected action type.
   */
  const onActionClick = (action: ActionType): void => {
    switch (action) {
      case ACTION_TYPES.APPOINTMENT:
        setIsMakingAppointment(true);
        break;

      case ACTION_TYPES.ALERT:
        setIsMakingAlert(true);
        break;

      case ACTION_TYPES.ACTIVITY:
        setIsMakingActivity(true);
        const newActivity = new UserActivity();
        newActivity.setActivityDetails('').setActivityTitle('').setActivityAffiliation(indId);
        setTempActivity(newActivity);
        break;
    }
  };

  const handleSelectedSection = (sectionIndex) => {
    if (['email', 'soa', 'flows'].includes(sections[sectionIndex].value)) {
      onSizeChange(SIDETAB_SIZE.LARGE);
    } else {
      onSizeChange(SIDETAB_SIZE.MEDIUM);
    }

    setSelectedSection(sectionIndex);
  };

  const handleSaveNote = async (body: string) => {
    const newNote = new IndNote();
    newNote
      .setIndNoteBody(body)
      .setIndNoteDate(Utilities.renderDateTimeLocal(new Date()))
      .setIndNoteAuthor(user.getAuthorName())
      .setIndNoteType('note')
      .setIndNoteInclude(false);

    const save = await individualNoteRepository.createIndNote(indId, newNote);

    newNote.setRawDate(Utilities.renderDateTimeLocal(new Date()));

    setIndNotes((prev) => [newNote, ...(prev || [])]);

    setIsAddingNote(false);

    return save;
  };

  const fetchNotes = (indId, offset = 0, limit = 10) => {
    individualNoteRepository
      .getIndNotes(indId, { limit: limit, offset: offset, criteria: { indNoteType: 'note' } })
      .then((response) => {
        setIndNotes(response.getItems());
      });
  };

  const handleSetUpIntegration = (serviceType: INTEGRATION_TYPE) => {
    // setActionFormShow(false);
    setShowIntegrationSetup(true);
    setAllowedIntegrationServices([...INTEGRATION_OPTIONS[serviceType]]);
  };

  const handleSetUpIntegrationHide = () => {
    setShowIntegrationSetup(false);
    setAllowedIntegrationServices([]);
    setAllowedVoIPServices([]);
    setAllowedSyncServices([]);
    setRefreshKey((prev) => prev + 1);
  };

  React.useEffect(() => {
    setIndNotes(null);
    setSelectedSection(null);
    individualRepository.getInd(indId, { include: include }).then((individual) => {
      setInd(individual);
    });
  }, [indId]);

  React.useEffect(() => {
    userFlagRepository.getUserFlags(userId).then((response) => {
      setUserFlags(response.getItems());
    });
  }, [userId]);

  React.useEffect(() => {
    if (sections[selectedSection]?.value === 'notes' && indNotes === null) {
      fetchNotes(indId, notesOffset, notesLimit);
    }
  }, [selectedSection, sections, indNotes, indId, notesOffset, notesLimit]);
  return (
    individual && (
      <>
        <h4 className="font-weight-bold mb-0">{individual.getIndName()}</h4>
        <div>
          <small>{individual.getIndStatus()}</small>
        </div>
        <div className="badge badge-pill badge-primary mt-2 mb-2">
          <a
            style={{ textDecoration: 'none' }}
            className="text-light"
            href={`/agent/profile/index/${individual.getIndId()}`}
          >
            View Profile
          </a>
        </div>
        <div className="badge badge-pill badge-primary mt-2 mb-2 ml-1" title="View in new window">
          <a
            style={{ textDecoration: 'none' }}
            className="text-light"
            target="_blank"
            href={`/agent/profile/index/${individual.getIndId()}`}
            rel="noreferrer"
          >
            <i className="far fa-external-link"></i>
          </a>
        </div>
        <div className="mt-2">
          <div className="mb-3">
            <div
              className="badge bg-contrast pr-3 pt-2 pb-2"
              style={{ borderRadius: '50rem', fontSize: '10pt' }}
            >
              {/*Appointment */}
              <OverlayTrigger
                placement="right"
                overlay={<Tooltip placement="right">Set appointment</Tooltip>}
              >
                <span className="ml-3" onClick={() => onActionClick(ACTION_TYPES.APPOINTMENT)}>
                  <i
                    className={`fas fa-calendar-alt text-secondary`}
                    style={{ fontSize: '14pt' }}
                  ></i>
                </span>
              </OverlayTrigger>

              {/*Activity */}
              <OverlayTrigger
                placement="right"
                overlay={<Tooltip placement="right">Set activity</Tooltip>}
              >
                <span className="ml-3" onClick={() => onActionClick(ACTION_TYPES.ACTIVITY)}>
                  <i className={`fas fa-tasks text-secondary`} style={{ fontSize: '14pt' }}></i>
                </span>
              </OverlayTrigger>

              {/*Alert */}
              <OverlayTrigger
                placement="right"
                overlay={<Tooltip placement="right">Set Alert</Tooltip>}
              >
                <span className="ml-3" onClick={() => onActionClick(ACTION_TYPES.ALERT)}>
                  <i
                    className={`fas fa-exclamation-circle text-secondary`}
                    style={{ fontSize: '14pt' }}
                  ></i>
                </span>
              </OverlayTrigger>

              {individual.getFlags().getItems().length > 0 && (
                <>
                  <span className="ml-3 border-right border-secondary" />

                  {individual
                    .getFlags()
                    .getItems()
                    .map((item) => (
                      <OverlayTrigger
                        key={item.getId?.() ?? `${item.getIndFlagged()}-${Math.random()}`}
                        placement="right"
                        overlay={
                          <Tooltip id={`flag-tip-${item.getId?.() ?? item.getIndFlagged()}`}>
                            {colorToMeaning[item.getIndFlagged()]}
                          </Tooltip>
                        }
                      >
                        <span className="ml-3">
                          <i
                            className={`fas fa-flag text-${item.getIndFlagged()}`}
                            style={{ fontSize: '14pt' }}
                          />
                        </span>
                      </OverlayTrigger>
                    ))}
                </>
              )}
            </div>
          </div>
          {individual
            .getPhones()
            .getItems()
            .map((item) => {
              return (
                <div className="mb-3">
                  <div
                    className="badge bg-contrast calMess border border-info p-2"
                    style={{ borderRadius: '50rem', fontSize: '10pt' }}
                  >
                    <i className="far fa-phone mr-2"></i>
                    {item.getIndPhoneNumber()}{' '}
                    <span className="badge badge-pill badge-info">{item.getIndPhoneType()}</span>
                  </div>
                </div>
              );
            })}
          {individual
            .getEmails()
            .getItems()
            .map((item) => {
              return (
                <div className="mb-3">
                  <div
                    className="badge bg-contrast calMess border border-success p-2"
                    style={{ borderRadius: '50rem', fontSize: '10pt' }}
                  >
                    <i className="far fa-at mr-2"></i>
                    {item.getIndEmailAddress()}{' '}
                    <span className="badge badge-pill badge-success">{item.getIndEmailType()}</span>
                  </div>
                </div>
              );
            })}
          {individual
            .getAddresses()
            .getItems()
            .map((item) => {
              const address = [
                item.getIndAddressStreet(),
                item.getIndAddressUnit(),
                item.getIndAddressState(),
                item.getIndAddressZip(),
              ];
              return (
                <div className="mb-3">
                  <div
                    className="badge bg-contrast calMess border border-primary p-2"
                    style={{ borderRadius: '50rem', fontSize: '10pt' }}
                  >
                    <i className="far fa-map-pin mr-2"></i>
                    {address.join(' ')}{' '}
                    <span className="badge badge-pill badge-primary">
                      {item.getIndAddressType()}
                    </span>
                  </div>
                </div>
              );
            })}
          {
            <div className="mb-3 mt-4">
              <div
                className="badge bg-contrast pr-3 pt-2 pb-2"
                style={{ borderRadius: '50rem', fontSize: '10pt' }}
              >
                {sections.map((section, index) => {
                  return (
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip placement="top">{section.title}</Tooltip>}
                    >
                      <i
                        className={`fas ${section.icon} ${selectedSection == index ? 'text-success' : ''} ml-1 cp ml-3`}
                        style={{ fontSize: '14pt' }}
                        onClick={() => handleSelectedSection(index)}
                      ></i>
                    </OverlayTrigger>
                  );
                })}
              </div>
            </div>
          }

          {selectedSection !== null && (
            <div className="bg-contrast rounded p-2 pb-3">
              {/*<div><b>{sections[selectedSection].title}</b></div>*/}

              {/* Policies */}
              {sections[selectedSection].value === 'policies' &&
                individual
                  .getPolicies()
                  .getItems()
                  .map((item) => {
                    const status = item.getPolicyStatus()?.toLowerCase();

                    if (status !== 'active' && status !== 'pending') {
                      return null;
                    }

                    return (
                      <div className="mt-2">
                        <div className="border border-secondary p-2 rounded">
                          <div>
                            <small>
                              <b>{item.getCompany().getCompanyName()}</b>
                            </small>
                          </div>
                          <div>
                            <small>{item.getPlan().getPlanName()}</small>
                          </div>
                          <div>
                            <span className="badge badge-pill badge-secondary">
                              {item.getPolicyType()}
                            </span>
                            <span
                              className={`badge badge-pill ml-2 badge-${status === 'active' ? 'success' : 'primary'}`}
                            >
                              {item.getPolicyStatus()}
                            </span>
                            <span className="badge badge-pill badge-info ml-2">
                              {item.getPolicyEffective()}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}

              {/* Notes */}
              {sections[selectedSection].value === 'notes' && (
                <div>
                  {indNotes === null ? (
                    <div className="alert small alert-info mt-3" role="alert">
                      <i className="fas fa-spinner fa-spin ml-2"></i> Loading...
                    </div>
                  ) : (
                    <>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <i>
                          <small>showing 10 most recent</small>
                        </i>
                        {!isAddingNote && (
                          <div>
                            <i
                              className="fas fa-plus-circle text-primary"
                              style={{ fontSize: '14pt', cursor: 'pointer' }}
                              onClick={() => setIsAddingNote(true)}
                            ></i>
                          </div>
                        )}
                      </div>

                      {isAddingNote && (
                        <NoteContentDisplay
                          note={(() => {
                            const newNote = new IndNote();
                            newNote.setIndNoteBody('');
                            newNote.setIndNoteAuthor(user.getAuthorName());
                            newNote.setIndNoteDate(new Date().toISOString());
                            return newNote;
                          })()}
                          isEditing={true}
                          onSave={handleSaveNote}
                          onCancel={() => setIsAddingNote(false)}
                        />
                      )}

                      {indNotes.length === 0 ? (
                        <p className="text-muted mt-3">No notes</p>
                      ) : (
                        indNotes
                          .slice(0, 10)
                          .map((item, idx) => (
                            <NoteContentDisplay key={idx} note={item} maxLength={300} />
                          ))
                      )}
                    </>
                  )}
                </div>
              )}

              {/* SMS */}
              {sections[selectedSection].value === 'sms' && (
                <SMSChat userAccessToken={userAccessToken} individual={individual} user={user} />
              )}

              {/* Email */}
              {sections[selectedSection].value === 'email' && (
                <EmailsCollection
                  userAccessToken={userAccessToken}
                  individual={individual}
                  user={user}
                  agencyId={usersAgencyId}
                  staffId={staffId}
                  onSetupClick={handleSetUpIntegration}
                />
              )}

              {/* Soa */}
              {sections[selectedSection].value === 'soa' && (
                <AgentIndProfileCmsFormsPanel
                  userId={userId}
                  userAccessToken={userAccessToken}
                  indId={individual.getIndId()}
                  agencyId={usersAgencyId}
                  framed={false}
                />
              )}

              {/* Flows */}
              {sections[selectedSection].value === 'flows' && (
                <IndFlowHistoryProfile
                  userId={userId}
                  userAccessToken={userAccessToken}
                  indId={indId}
                  activeOnly={false}
                  forProfile={false}
                  operatorName={operatorName}
                />
              )}

              {/* Enroll */}
              {sections[selectedSection].value === 'enroll' && (
                <AgentEnrollmentPanel
                  userId={userId}
                  userAccessToken={userAccessToken}
                  agencyId={usersAgencyId}
                  uplineId={null}
                  absoluteUplineId={null}
                  indId={individual.getIndId()}
                  stack={true}
                  // framed={false}
                />
              )}

              {/* Logs Section */}
              {sections[selectedSection].value === 'logs' && (
                <div>
                  <div className="mb-2 mt-2">
                    {PROFILE_LOG_PORTAL_TAB_LABELS.map((tab) => {
                      const classList = [
                        'badge',
                        'badge-sm',
                        'border',
                        'border-info',
                        'mr-1',
                        'cp',
                        tab.value === selectedLogSection ? 'badge-info' : '',
                      ];

                      return (
                        <div
                          key={tab.value}
                          onClick={() => setSelectedLogSection(tab.value)}
                          className={classList.join(' ').trim()}
                        >
                          {tab.label}
                        </div>
                      );
                    })}
                  </div>
                  {selectedLogSection === PROFILE_LOG_TAB.CALL_LOG && (
                    <ProfileNotesLogsCalls
                      userId={userId}
                      indId={indId}
                      userAccessToken={userAccessToken}
                      onSetupClick={handleSetUpIntegration}
                    />
                  )}
                  {selectedLogSection === PROFILE_LOG_TAB.SMS_LOG && (
                    <ProfileNotesLogsSMS
                      userId={userId}
                      indId={indId}
                      userAccessToken={userAccessToken}
                      onSetupClick={handleSetUpIntegration}
                    />
                  )}
                  {selectedLogSection === PROFILE_LOG_TAB.OTHER_LOGS && (
                    <ProfileNotesLogs
                      userId={userId}
                      indId={indId}
                      userAccessToken={userAccessToken}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {isMakingAppointment && (
          <CalendarCreateEventButton
            indId={indId}
            directInstantiation={true}
            onClose={() => setIsMakingAppointment(false)}
          />
        )}

        {isMakingAlert && (
          <AlertModal
            userAccessToken={userAccessToken}
            indId={indId}
            userId={userId}
            onClose={() => setIsMakingAlert(false)}
          />
        )}

        {isMakingActivity && (
          <ActivityModal
            userAccessToken={userAccessToken}
            userId={userId}
            agencyId={usersAgencyId}
            directUplineId={directUplineId}
            absoluteUplineId={absoluteUplineId}
            staffId={staffId}
            indId={indId}
            activity={tempActivity}
            show={true}
            onClose={() => setIsMakingActivity(false)}
          />
        )}

        {/* Integration Manager */}
        <IntegrationManager
          userAccessToken={userAccessToken}
          userId={userId}
          showIntegrationSetup={showIntegrationSetup}
          onHide={handleSetUpIntegrationHide}
          allowedServices={allowedIntegrationServices}
        />
      </>
    )
  );
};
